import React, { useState } from "react";
import { ArrowRight, Clock, Users, MapPin, BookOpen, ExternalLink } from "lucide-react";
import PageHero from "@/components/PageHero";
import FloatingEditButton from "@/components/FloatingEditButton";
import ShareButtons from "@/components/ShareButtons";
import { AlbumCompacto } from "@/components/GaleriaFotos";
import EnrollmentModal from "@/components/EnrollmentModal";
import SEO from "@/components/SEO";
import Reveal from "@/components/motion/Reveal";
import Section, { SectionHeading } from "@/components/ui/Section";
import Card, { CardBody } from "@/components/ui/Card";
import CTAButton from "@/components/ui/CTAButton";
import FilterPills from "@/components/ui/FilterPills";
import SmartImage from "@/components/ui/SmartImage";
import EmptyState from "@/components/ui/EmptyState";
import { CardGridSkeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";
import useCachedResource from "@/hooks/useCachedResource";

const levelColors = {
  basico: "bg-green-100 text-green-700",
  medio: "bg-amber-100 text-amber-700",
  avanzado: "bg-red-100 text-red-700",
};

const levelLabels = { basico: "Básico", medio: "Medio", avanzado: "Avanzado" };
const modalityLabels = { presencial: "Presencial", online: "Online", hibrido: "Híbrido" };

const NIVELES = [
  { value: "todos", label: "Todos" },
  { value: "basico", label: "Básico" },
  { value: "medio", label: "Medio" },
  { value: "avanzado", label: "Avanzado" },
];

const itinerary = [
  { step: "01", title: "Evangelizador Digital Principiante", desc: "Fundamentos de redes sociales y presencia cristiana online. Para todos los agentes pastorales." },
  { step: "02", title: "Creador de Contenido Pastoral", desc: "Diseño, fotografía, vídeo y copywriting al servicio del Evangelio." },
  { step: "03", title: "Estratega Digital Misionero", desc: "Estrategia, analítica y gestión de comunidades digitales con criterios evangélicos." },
  { step: "04", title: "Innovador con IA y Nuevas Tecnologías", desc: "Inteligencia artificial, automatización y proyectos digitales de vanguardia para la evangelización." },
];

const PUBLICOS = [
  { title: "Sacerdotes", desc: "Formación específica para usar las herramientas digitales en la pastoral parroquial." },
  { title: "Catequistas", desc: "Aprende a catequizar en el entorno digital y acompañar a tus grupos online." },
  { title: "Jóvenes", desc: "Conviértete en misionero digital y evangeliza desde tu smartphone." },
  { title: "Agentes Pastorales", desc: "Herramientas digitales para potenciar tu trabajo en la parroquia o movimiento." },
];

export default function Formacion() {
  // Antes no había estado de carga: mientras llegaba la respuesta se pintaba
  // «No hay cursos disponibles en este nivel por ahora» y luego saltaba a la
  // rejilla. El visitante leía un mensaje falso en cada visita.
  const { data: courses, loading } = useCachedResource("courses", () => apiGet("/courses"));
  const [activeLevel, setActiveLevel] = useState("todos");
  const [enrollModal, setEnrollModal] = useState(null);

  const cursos = Array.isArray(courses) ? courses : [];
  const filtered = activeLevel === "todos" ? cursos : cursos.filter(c => c.level === activeLevel);

  return (
    <div data-testid="formacion-page">
      <SEO
        title="Formación para evangelizadores digitales"
        description="Cursos e itinerario formativo para sacerdotes, catequistas, jóvenes y agentes pastorales que quieren evangelizar en el entorno digital."
      />
      <PageHero slug="formacion" />

      {/* Itinerario */}
      <Section tone="light">
        <Reveal>
          <SectionHeading
            label="Tu camino formativo"
            title="Itinerario del Evangelizador Digital"
            align="center"
          />
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {itinerary.map((item, i) => (
            <Reveal key={i} delay={i * 70} distance={28}>
              <Card data-testid={`itinerary-step-${i}`} className="p-6 relative h-full" interactive>
                <span className="font-heading font-bold text-5xl text-border absolute top-4 right-4" aria-hidden="true">
                  {item.step}
                </span>
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center mb-4 shadow-sm">
                  <span className="text-white font-bold text-xs">{item.step}</span>
                </div>
                <h3 className="font-heading font-bold text-sm mb-2 leading-snug">{item.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
              </Card>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Cursos */}
      <Section tone="muted" id="cursos">
        <Reveal>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-12 gap-4">
            <SectionHeading
              label="Próximas convocatorias"
              title="Cursos Disponibles"
              className="mb-0"
            />
            <FilterPills
              options={NIVELES}
              value={activeLevel}
              onChange={setActiveLevel}
              label="Filtrar cursos por nivel"
              data-testid="course-level-filter"
            />
          </div>
        </Reveal>

        {loading && !cursos.length ? (
          <CardGridSkeleton count={3} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="No hay cursos en este nivel"
            description="Todavía no hay convocatorias abiertas para este nivel. Escríbenos y te avisamos en cuanto haya una."
            action={<CTAButton to="/contacto">Contáctanos</CTAButton>}
          />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((c, i) => (
              <Reveal key={c._id || i} delay={Math.min(i, 5) * 70} distance={28}>
                <Card data-testid={`course-card-${i}`} className="h-full flex flex-col" interactive>
                  {c.image && <SmartImage src={c.image} alt="" ratio="16 / 9" />}
                  <CardBody className="flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${levelColors[c.level] || "bg-muted text-muted-foreground"}`}>
                        {levelLabels[c.level] || c.level}
                      </span>
                      <span className="text-xs text-muted-foreground">{modalityLabels[c.modality] || c.modality}</span>
                    </div>
                    <h3 className="font-heading font-bold text-base mb-2">{c.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{c.description}</p>
                    <div className="space-y-1.5 mb-4">
                      {c.duration && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Clock className="w-3.5 h-3.5" aria-hidden="true" />{c.duration}</div>}
                      {c.target && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Users className="w-3.5 h-3.5" aria-hidden="true" />{c.target}</div>}
                      {c.start_date && <div className="flex items-center gap-2 text-xs text-muted-foreground"><MapPin className="w-3.5 h-3.5" aria-hidden="true" />Inicio: {c.start_date}</div>}
                    </div>
                    <div className="mt-auto">
                      <AlbumCompacto fotos={c.gallery} className="mb-4" />
                      {c.registration_url ? (
                        <CTAButton href={c.registration_url} size="sm" className="font-semibold"
                          data-testid={`course-register-${i}`}>
                          Inscribirse <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                        </CTAButton>
                      ) : (
                        <CTAButton
                          onClick={() => setEnrollModal({ id: c.id || c._id, title: c.title })}
                          size="sm"
                          className="font-semibold"
                          data-testid={`course-enroll-btn-${i}`}
                        >
                          Inscribirme <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                        </CTAButton>
                      )}
                      <div className="mt-4 pt-3 border-t border-border flex justify-end">
                        <ShareButtons
                          compact
                          url="/formacion"
                          title={c.title}
                          description={c.description || ""}
                        />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Reveal>
            ))}
          </div>
        )}
      </Section>

      {/* Públicos */}
      <Section tone="light">
        <Reveal>
          <SectionHeading title="Formación para Todos" align="center" />
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PUBLICOS.map((t, i) => (
            <Reveal key={i} delay={i * 70} distance={24}>
              <Card className="p-6 h-full">
                <h3 className="font-heading font-bold text-base mb-2">{t.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t.desc}</p>
              </Card>
            </Reveal>
          ))}
        </div>
        <Reveal delay={140} className="text-center mt-12 flex justify-center">
          <CTAButton to="/contacto" size="lg" className="font-semibold">
            Solicitar más información <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </CTAButton>
        </Reveal>
      </Section>

      <FloatingEditButton slug="formacion" />
      {enrollModal && (
        <EnrollmentModal
          itemId={enrollModal.id}
          itemTitle={enrollModal.title}
          itemType="curso"
          onClose={() => setEnrollModal(null)}
        />
      )}
    </div>
  );
}
